import {
  MAutocomplete,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { toExponentialUppercase } from "@app/utils/functions";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  useIsotopos,
  useIsotoposInstalacion,
} from "@services/servicesGenericsTables";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { columnsGammaForm } from "../../utils/background.columns";
import { TableRowGammaForm } from "../../utils/types";
import { useBackground } from "../../utils/useBackground";
import { KeysTabsAnalysis } from "@app/types/samples.types";
import { monthsTable } from "@app/utils/tables/months.tables";
import { yearsTable } from "@app/utils/tables/years.tables";
import { mean, std } from "mathjs";
import FileUploadRPTFondos from "../fileUploadRPTFondos";
import { BackgroundListType, BackgroundMappingType } from "@app/types/background.types";

const GammaForm = ({ disable }: { disable: boolean }) => {
  const { getValues, watch, setValue } = useFormContext();
  const { isotoposInstalacionList } = useIsotoposInstalacion();
  const { deleteFondoCalculation, fondosList } = useBackground();
  const { isotoposList } = useIsotopos();
  const tableRows = watch("tableData") || [];
  
  // Estado para el diálogo de confirmación
  const [showMoreCountsDialog, setShowMoreCountsDialog] = useState(false);
  const [moreCountsCallback, setMoreCountsCallback] = useState<((willHaveMoreCounts: boolean) => void) | null>(null);

  const selectedIsotope = watch("isotope_instalation");
  const selectedEquipment = watch("equipment");
  const type = watch("type");
  const currentFondoId = watch("id"); // ID del fondo que estamos editando (undefined si es nuevo)
  const currentCalculationDate = watch("calculation_date"); // Fecha de cálculo del fondo actual
  const currentAnalysisType = watch("analysis_type"); // Tipo de análisis actual
  const currentInstalacion = watch("instalation"); // Instalación actual
  const fondoIntegralActual = watch("fondo_integral_current_reference");
  const countingTime = watch("counting_time");
  const fondoIntegralReferenciaCps = watch("fondo_integral_reference");
  const fondoIntegralActualCps = watch(
    "fondo_integral_current_reference_calculated"
  );
  const fondoAreaActualCounts = watch("fondo_area_current_counts");
  const fondoAreaCurrentCps = watch("fondo_area_current_cps");
  const fondoAreaReferenciaCps = watch("fondo_area_reference");
  const fondoAreaVariation = watch("fondo_area_variation");
  const analysisType = watch("analysis_type");
  const firstIntegralFondo = watch("integral_fondo_1");
  const firstFondoCountingTime = watch("counting_time");
  const secondIntegralFondo = watch("integral_fondo_2");
  const secondFondoCountingTime = watch("counting_time_fondo_2");
  const firstCpsIntegralFondo = watch("cps_integral_fondo_1");
  const secondCpsIntegralFondo = watch("cps_integral_fondo_2");
  const errorFondoAreaReferencia = watch("fondo_area_reference_error");
  const errorFondoAreaActual = watch("fondo_area_error");
  let firstCpsIntegralFondoValue = 0;
  let secondCpsIntegralFondoValue = 0;

  // Función para buscar el último fondo válido del mismo mes, año, tipo de análisis e instalación
  const getLatestFondoFromList = React.useMemo(() => {
    // Si no hay fondos en la lista, retornar null
    if (!fondosList || fondosList.length === 0) {
      return null;
    }

    // Si no tenemos los datos mínimos para filtrar, retornar null
    if (!selectedEquipment || !type) {
      return null;
    }

    const equipmentId = typeof selectedEquipment === 'object' ? selectedEquipment.id : selectedEquipment;
    const typeValue = typeof type === 'object' ? type.value : type;

    // Filtrar fondos que cumplan las condiciones disponibles
    const validFondos = fondosList.filter((fondo) => {
      // Verificar equipo y tipo (condiciones mínimas)
      const fondoEquipmentId = typeof fondo.equipment === 'object' ? fondo.equipment?.id : fondo.equipment;
      const fondoTypeValue = typeof fondo.type === 'string' ? fondo.type : null;
      
      if (fondoEquipmentId !== equipmentId || fondoTypeValue !== typeValue) {
        return false;
      }

      // Si estamos editando un fondo, solo incluir fondos con ID inferior
      if (currentFondoId && fondo.id >= currentFondoId) {
        return false;
      }

      // Si tenemos fecha de cálculo, filtrar por mes y año
      /*if (currentCalculationDate && fondo.calculation_date) {
        const currentDate = new Date(currentCalculationDate);
        const fondoDate = new Date(fondo.calculation_date);
        if (currentDate.getMonth() !== fondoDate.getMonth() || 
            currentDate.getFullYear() !== fondoDate.getFullYear()) {
          return false;
        }
      }*/

      // Si tenemos tipo de análisis, filtrar por él
      if (currentAnalysisType) {
        const analysisTypeId = typeof currentAnalysisType === 'object' ? currentAnalysisType.id : currentAnalysisType;
        const fondoAnalysisTypeId = typeof fondo.analysis_type === 'object' ? fondo.analysis_type?.id : fondo.analysis_type;
        if (fondoAnalysisTypeId !== analysisTypeId) {
          return false;
        }
      }

      // Si tenemos instalación, filtrar por ella
      if (currentInstalacion) {
        const instalacionId = typeof currentInstalacion === 'object' ? currentInstalacion.id : currentInstalacion;
        const fondoInstalacionId = typeof fondo.instalation === 'object' ? fondo.instalation?.id : fondo.instalation;
        if (fondoInstalacionId !== instalacionId) {
          return false;
        }
      }

      return true;
    });

    // Retornar el último (más reciente por ID)
    if (validFondos.length === 0) {
      return null;
    }
    
    return validFondos.reduce((latest, fondo) => 
      (fondo.id > latest.id) ? fondo : latest
    );
  }, [fondosList, currentCalculationDate, currentAnalysisType, currentInstalacion, currentFondoId, selectedEquipment, type]);

  // Cálculo de Fondo Integral actual (cps)
  useEffect(() => {
    if (fondoIntegralActual && countingTime) {
      const calculatedValue =
        parseFloat(fondoIntegralActual) / parseFloat(countingTime);

      setValue(
        "fondo_integral_current_reference_calculated",
        calculatedValue
      );
    }
  }, [fondoIntegralActual, countingTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cálculo de Fondo Integral (%var)
  useEffect(() => {
    if (fondoIntegralActualCps && fondoIntegralReferenciaCps) {
      const actual = parseFloat(fondoIntegralActualCps);
      const reference = parseFloat(fondoIntegralReferenciaCps);

      if (reference !== 0) {
        const percentageVariation = ((actual - reference) / reference) * 100;
        setValue("fondo_integral", percentageVariation);
      }
    }
  }, [fondoIntegralActualCps, fondoIntegralReferenciaCps]);

  // Cálculo de Variación (%) LID
  useEffect(() => {
    if (fondoIntegralActualCps && fondoIntegralReferenciaCps) {
      const actual = parseFloat(fondoIntegralActualCps);
      const reference = parseFloat(fondoIntegralReferenciaCps);

      if (reference !== 0) {
        const lidVariation = (Math.sqrt(actual / reference) - 1) * 100;
        setValue("lid_variation", lidVariation);
      }
    }
  }, [fondoIntegralActualCps, fondoIntegralReferenciaCps]);

  // Cálculo de Fondo Área actual (cps)
  useEffect(() => {
    if (fondoAreaActualCounts !== undefined && countingTime) {
      const counts = parseFloat(fondoAreaActualCounts) || 0;
      const time = parseFloat(countingTime);
      
      if (time !== 0) {
        const calculatedValue = counts / time;
        setValue("fondo_area_current_cps", calculatedValue);
      } else {
        setValue("fondo_area_current_cps", 0);
      }
    } else if (fondoAreaActualCounts === undefined || fondoAreaActualCounts === "") {
      setValue("fondo_area_current_cps", 0);
    }
  }, [fondoAreaActualCounts, countingTime]);

  // Cálculo de Error Fondo Área (cps)
  useEffect(() => {
    if (fondoAreaActualCounts !== undefined && countingTime) {
      const counts = parseFloat(fondoAreaActualCounts) || 0;
      const time = parseFloat(countingTime);
      
      if (time !== 0) {
        const errorValue = Math.sqrt(counts) / time;
        setValue("fondo_area_error", errorValue);
      } else {
        setValue("fondo_area_error", 0);
      }
    } else if (fondoAreaActualCounts === undefined || fondoAreaActualCounts === "") {
      setValue("fondo_area_error", 0);
    }
  }, [fondoAreaActualCounts, countingTime]);

  // Cálculo de Variación (%) Fondo Área
  useEffect(() => {
    if (
      fondoAreaCurrentCps !== undefined &&
      fondoAreaReferenciaCps !== undefined &&
      fondoIntegralReferenciaCps
    ) {
        const currentCps = parseFloat(fondoAreaCurrentCps) || 0;
        const referenceCps = parseFloat(fondoAreaReferenciaCps) || 0;
        const integralRefCps = parseFloat(fondoIntegralReferenciaCps);
        
        if (integralRefCps !== 0) {
          const variationValue = ((currentCps - referenceCps) / integralRefCps) * 100;
          setValue("fondo_area_variation", variationValue);
        } else {
          setValue("fondo_area_variation", 0);
        }
    }else{
      setValue("fondo_area_variation", 0);
    }
  }, [
    fondoAreaCurrentCps,
    fondoAreaReferenciaCps,
    fondoIntegralReferenciaCps,
  ]);

  // Cálculo de A aplicar Fondo Integral (cps)
  // Implementa el flujo de casuísticas según documento de especificaciones
  useEffect(() => {
    if (
      fondoIntegralActualCps &&
      fondoIntegralReferenciaCps
    ) {
      const currentIsotopeId = selectedIsotope?.id;
      if (!currentIsotopeId) return;
      
      const referenceCps = parseFloat(fondoIntegralReferenciaCps);
      const actualCps = parseFloat(fondoIntegralActualCps);
      
      // =================================================================
      // PASO 1: Obtener TODOS los contajes del mismo isótopo en el mes actual
      // =================================================================
      const allIntegralCpsValues: number[] = [];
      
      // Si existe un fondo válido del mismo mes, año, tipo de análisis e instalación
      // ya viene filtrado excluyendo el fondo que estamos editando
      if (getLatestFondoFromList && getLatestFondoFromList.fondo_calculation) {
        getLatestFondoFromList.fondo_calculation.forEach((calc: any) => {
          if (calc.isotope_instalation?.id === currentIsotopeId) {
            const integralCps = parseFloat(calc.fondo_integral_current_reference_calculated || calc.fondo_integration_to_apply) || 0;
            if (integralCps > 0) allIntegralCpsValues.push(integralCps);
          }
        });
      }
      
      // Agregar el contaje actual
      allIntegralCpsValues.push(actualCps);
      
      // Calcular la media de TODOS los contajes del mes
      const mediaIntegralCps = allIntegralCpsValues.reduce((a, b) => a + b, 0) / allIntegralCpsValues.length;
      
      // =================================================================
      // PASO 2: Calcular variaciones de CADA contaje frente a referencia
      // =================================================================
      
      // Variación de la MEDIA usando fórmula LID
      let variacionMediaIntegral = 0;
      if (referenceCps !== 0) {
        variacionMediaIntegral = (Math.sqrt(mediaIntegralCps / referenceCps) - 1) * 100;
      }
      
      // Calcular variaciones individuales de CADA contaje del mes
      const variacionesIntegrales: number[] = [];
      allIntegralCpsValues.forEach((cps) => {
        if (referenceCps !== 0) {
          const variacion = (Math.sqrt(cps / referenceCps) - 1) * 100;
          variacionesIntegrales.push(variacion);
        }
      });
      
      const numContajesEnMes = allIntegralCpsValues.length;
      
      let valueToApply = 0;
      
      // =================================================================
      // PASO 3: APLICAR REGLAS SEGÚN NÚMERO DE CONTAJES EN EL MES
      // =================================================================
      
      if (numContajesEnMes >= 2) {
        // ============================================================
        // HAY 2 O MÁS CONTAJES EN EL MES → CASOS I, II, III
        // ============================================================
        
        // Verificar si TODOS los contajes tienen variación <= ±5%
        const todosConVariacionBaja = variacionesIntegrales.every(v => Math.abs(v) <= 5);
        
        if (todosConVariacionBaja) {
          // CASO I: Todos con variación <= ±5% → MANTENER referencia
          valueToApply = referenceCps;
        } else {
          // Al menos uno tiene variación > ±5%
          // Verificar si hay contajes con variación > ±5%
          const contajesAltos = variacionesIntegrales.filter(v => Math.abs(v) > 5);
          
          if (contajesAltos.length >= 2) {
            // Hay al menos 2 contajes con variación > ±5%
            const signosIguales = contajesAltos.every(v => v * contajesAltos[0] > 0);
            
            if (signosIguales) {
              // CASO III: Variación > ±5% con igual signo → CAMBIAR (aplicar MEDIA)
              valueToApply = mediaIntegralCps;
            } else {
              // CASO II: Variación > ±5% con distinto signo → MANTENER referencia
              valueToApply = referenceCps;
            }
          } else {
            // Solo 1 contaje con variación > ±5% → MANTENER referencia
            valueToApply = referenceCps;
          }
        }
        
      } else {
        // ============================================================
        // HAY SOLO 1 CONTAJE EN EL MES → CASOS IV, V, VI, VII, VIII
        // ============================================================
        
        if (Math.abs(variacionMediaIntegral) <= 5) {
          // CASO IV: Variación ≤ ±5% → MANTENER valor de referencia
          valueToApply = referenceCps;
        } else {
          // Variación > ±5% → Buscar contaje anterior (de cualquier mes)
          // Buscar en fondosList el fondo anterior del mismo equipo
          const allFondosDelEquipo = fondosList.filter((fondo: any) => {
            const fondoEquipmentId = typeof fondo.equipment === 'object' ? fondo.equipment?.id : fondo.equipment;
            const equipmentId = typeof selectedEquipment === 'object' ? selectedEquipment.id : selectedEquipment;
            const fondoTypeValue = typeof fondo.type === 'string' ? fondo.type : null;
            const typeValue = typeof type === 'object' ? type.value : type;
            
            if (fondoEquipmentId !== equipmentId || fondoTypeValue !== typeValue) {
              return false;
            }
            
            if (currentFondoId && fondo.id >= currentFondoId) {
              return false;
            }
            
            return true;
          }).sort((a: any, b: any) => {
            const dateA = new Date(a.year, a.month - 1);
            const dateB = new Date(b.year, b.month - 1);
            return dateB.getTime() - dateA.getTime();
          });
          
          if (allFondosDelEquipo.length > 0) {
            const fondoAnterior = allFondosDelEquipo[0];
            const calcAnterior = fondoAnterior.fondo_calculation?.find(
              (calc: any) => calc.isotope_instalation?.id === currentIsotopeId
            );
            
            if (calcAnterior) {
              // Obtener datos del contaje anterior
              const prevMediaIntegral = parseFloat(String(calcAnterior.fondo_integration_to_apply || calcAnterior.fondo_integral_current_reference_calculated || 0));
              const prevReferenceCps = parseFloat(String(calcAnterior.fondo_integral_reference || 0));
              
              // Calcular variación del contaje anterior usando fórmula LID
              let prevVariacion = 0;
              if (prevReferenceCps !== 0) {
                prevVariacion = (Math.sqrt(prevMediaIntegral / prevReferenceCps) - 1) * 100;
              }
              
              const currentSign = variacionMediaIntegral >= 0 ? 1 : -1;
              const prevSign = prevVariacion >= 0 ? 1 : -1;
              const sameSign = currentSign === prevSign;
              const sameReference = Math.abs(referenceCps - prevReferenceCps) < 0.0001;
              
              if (Math.abs(prevVariacion) <= 5) {
                // CASO V: Contaje anterior con variación ≤ ±5% → MANTENER referencia
                valueToApply = referenceCps;
              } else if (!sameSign) {
                // CASO VI: Contaje anterior con variación > ±5% y distinto signo → MANTENER referencia
                valueToApply = referenceCps;
              } else if (!sameReference) {
                // CASO VII: Contaje anterior con variación > ±5%, mismo signo pero distinta referencia → MANTENER referencia
                valueToApply = referenceCps;
              } else {
                // CASO VIII: Contaje anterior con variación > ±5%, mismo signo, misma referencia → CAMBIAR
                valueToApply = mediaIntegralCps;
              }
            } else {
              // No hay datos del isótopo anterior: MANTENER referencia
              valueToApply = referenceCps;
            }
          } else {
            // No hay fondo anterior: MANTENER referencia
            valueToApply = referenceCps;
          }
        }
      }
      
      setValue(
        "fondo_integration_to_apply",
        valueToApply
      );
    }else{
      setValue(
        "fondo_integration_to_apply",
        0
      );
    }
  }, [fondoIntegralActualCps, fondoIntegralReferenciaCps, getLatestFondoFromList, tableRows, selectedIsotope, fondosList, selectedEquipment, type, currentFondoId]);

  // Cálculo de A aplicar Fondo Área (cps)
  // Implementa el flujo de casuísticas según documento de especificaciones
  useEffect(() => {
    if (fondoAreaCurrentCps !== undefined && fondoAreaReferenciaCps !== undefined && fondoIntegralReferenciaCps !== undefined) {
      const currentIsotopeId = selectedIsotope?.id;
      if (!currentIsotopeId) return;
      
      const integralReferenceCps = parseFloat(fondoIntegralReferenciaCps);
      const areaReferenceCps = parseFloat(fondoAreaReferenciaCps);
      const areaActualCps = parseFloat(fondoAreaCurrentCps);
      const errorActual = errorFondoAreaActual ? parseFloat(errorFondoAreaActual) : 0;
      const errorReferencia = errorFondoAreaReferencia ? parseFloat(errorFondoAreaReferencia) : 0;
      
      // =================================================================
      // PASO 1: Obtener TODOS los contajes del mismo isótopo en el mes actual
      // =================================================================
      const allAreaCpsValues: number[] = [];
      const allAreaErrorValues: number[] = [];
      
      // Si existe un fondo válido del mismo mes, año, tipo de análisis e instalación
      // ya viene filtrado excluyendo el fondo que estamos editando
      if (getLatestFondoFromList && getLatestFondoFromList.fondo_calculation) {
        getLatestFondoFromList.fondo_calculation.forEach((calc: any) => {
          if (calc.isotope_instalation?.id === currentIsotopeId) {
            const areaCps = parseFloat(calc.fondo_area_current_cps || calc.fondo_area_to_apply) || 0;
            const errorCps = parseFloat(calc.fondo_area_error || calc.fondo_area_error_to_apply) || 0;
            if (areaCps > 0) allAreaCpsValues.push(areaCps);
            if (errorCps > 0) allAreaErrorValues.push(errorCps);
          }
        });
      }
      
      // Agregar el contaje actual
      allAreaCpsValues.push(areaActualCps);
      allAreaErrorValues.push(errorActual);
      
      // Calcular la media de TODOS los contajes del mes
      const mediaAreaCps = allAreaCpsValues.reduce((a, b) => a + b, 0) / allAreaCpsValues.length;
      
      // Calcular desviación típica de los valores de fondo área (CPS) para usar como error
      // Usa 'unbiased' (divide por n-1) para coincidir con DESVEST de Excel
      const desviacionTipicaAreaCps = allAreaCpsValues.length > 1 
        ? std(allAreaCpsValues, 'unbiased') as number
        : errorActual;
      
      // =================================================================
      // PASO 2: Calcular variaciones de CADA contaje frente a referencia
      // =================================================================
      
      // Variación de la MEDIA para área usando fondo integral en denominador
      let variacionMediaArea = 0;
      if (integralReferenceCps !== 0) {
        variacionMediaArea = ((mediaAreaCps - areaReferenceCps) / integralReferenceCps) * 100;
      }
      
      // Calcular variaciones individuales de CADA contaje del mes
      const variacionesAreas: number[] = [];
      allAreaCpsValues.forEach((cps) => {
        if (integralReferenceCps !== 0) {
          const variacion = ((cps - areaReferenceCps) / integralReferenceCps) * 100;
          variacionesAreas.push(variacion);
        }
      });
      
      const numContajesEnMes = allAreaCpsValues.length;
      
      let valueToApply = 0;
      let errorToApply = 0;
      
      // =================================================================
      // PASO 3: APLICAR REGLAS SEGÚN NÚMERO DE CONTAJES EN EL MES
      // =================================================================
      
      if (numContajesEnMes >= 2) {
        // ============================================================
        // HAY 2 O MÁS CONTAJES EN EL MES → CASOS I, II, III
        // ============================================================
        
        // Verificar si TODOS los contajes tienen variación <= ±5%
        const todosConVariacionBaja = variacionesAreas.every(v => Math.abs(v) <= 5);
        
        if (todosConVariacionBaja) {
          // CASO I: Todos con variación <= ±5% → MANTENER referencia
          valueToApply = areaReferenceCps;
          errorToApply = errorReferencia;
        } else {
          // Al menos uno tiene variación > ±5%
          // Verificar si hay contajes con variación > ±5%
          const contajesAltos = variacionesAreas.filter(v => Math.abs(v) > 5);
          
          if (contajesAltos.length >= 2) {
            // Hay al menos 2 contajes con variación > ±5%
            const signosIguales = contajesAltos.every(v => v * contajesAltos[0] > 0);
            
            if (signosIguales) {
              // CASO III: Variación > ±5% con igual signo → CAMBIAR (aplicar MEDIA)
              valueToApply = mediaAreaCps;
              errorToApply = desviacionTipicaAreaCps;
            } else {
              // CASO II: Variación > ±5% con distinto signo → MANTENER referencia
              valueToApply = areaReferenceCps;
              errorToApply = errorReferencia;
            }
          } else {
            // Solo 1 contaje con variación > ±5% → MANTENER referencia
            valueToApply = areaReferenceCps;
            errorToApply = errorReferencia;
          }
        }
        
      } else {
        // ============================================================
        // HAY SOLO 1 CONTAJE EN EL MES → CASOS IV, V, VI, VII, VIII
        // ============================================================
        
        if (Math.abs(variacionMediaArea) <= 5) {
          // CASO IV: Variación ≤ ±5% → MANTENER valor de referencia
          valueToApply = areaReferenceCps;
          errorToApply = errorReferencia;
        } else {
          // Variación > ±5% → Buscar contaje anterior (de cualquier mes)
          // Buscar en fondosList el fondo anterior del mismo equipo
          const allFondosDelEquipo = fondosList.filter((fondo: any) => {
            const fondoEquipmentId = typeof fondo.equipment === 'object' ? fondo.equipment?.id : fondo.equipment;
            const equipmentId = typeof selectedEquipment === 'object' ? selectedEquipment.id : selectedEquipment;
            const fondoTypeValue = typeof fondo.type === 'string' ? fondo.type : null;
            const typeValue = typeof type === 'object' ? type.value : type;
            
            if (fondoEquipmentId !== equipmentId || fondoTypeValue !== typeValue) {
              return false;
            }
            
            if (currentFondoId && fondo.id >= currentFondoId) {
              return false;
            }
            
            return true;
          }).sort((a: any, b: any) => {
            const dateA = new Date(a.year, a.month - 1);
            const dateB = new Date(b.year, b.month - 1);
            return dateB.getTime() - dateA.getTime();
          });
          
          if (allFondosDelEquipo.length > 0) {
            const fondoAnterior = allFondosDelEquipo[0];
            const calcAnterior = fondoAnterior.fondo_calculation?.find(
              (calc: any) => calc.isotope_instalation?.id === currentIsotopeId
            );
            
            if (calcAnterior) {
              // Obtener datos del contaje anterior
              const prevMediaArea = parseFloat(String(calcAnterior.fondo_area_to_apply || calcAnterior.fondo_area_current_cps || 0));
              const prevAreaReferenceCps = parseFloat(String(calcAnterior.fondo_area_reference || 0));
              const prevIntegralReferenceCps = parseFloat(String(calcAnterior.fondo_integral_reference || 0));
              
              // Calcular variación del contaje anterior
              let prevVariacion = 0;
              if (prevIntegralReferenceCps !== 0) {
                prevVariacion = ((prevMediaArea - prevAreaReferenceCps) / prevIntegralReferenceCps) * 100;
              }
              
              const currentSign = variacionMediaArea >= 0 ? 1 : -1;
              const prevSign = prevVariacion >= 0 ? 1 : -1;
              const sameSign = currentSign === prevSign;
              const sameReference = Math.abs(areaReferenceCps - prevAreaReferenceCps) < 0.0001;
              
              if (Math.abs(prevVariacion) <= 5) {
                // CASO V: Contaje anterior con variación ≤ ±5% → MANTENER referencia
                valueToApply = areaReferenceCps;
                errorToApply = parseFloat(String(calcAnterior.fondo_area_error_to_apply || errorReferencia));
              } else if (!sameSign) {
                // CASO VI: Contaje anterior con variación > ±5% y distinto signo → MANTENER referencia
                valueToApply = areaReferenceCps;
                errorToApply = parseFloat(String(calcAnterior.fondo_area_error_to_apply || errorReferencia));
              } else if (!sameReference) {
                // CASO VII: Contaje anterior con variación > ±5%, mismo signo pero distinta referencia → MANTENER referencia
                valueToApply = areaReferenceCps;
                errorToApply = parseFloat(String(calcAnterior.fondo_area_error_to_apply || errorReferencia));
              } else {
                // CASO VIII: Contaje anterior con variación > ±5%, mismo signo, misma referencia → CAMBIAR
                valueToApply = mediaAreaCps;
                errorToApply = desviacionTipicaAreaCps;
              }
            } else {
              // No hay datos del isótopo anterior: MANTENER referencia
              valueToApply = areaReferenceCps;
              errorToApply = errorReferencia;
            }
          } else {
            // No hay fondo anterior: MANTENER referencia
            valueToApply = areaReferenceCps;
            errorToApply = errorReferencia;
          }
        }
      }

      setValue("fondo_area_to_apply", valueToApply);
      setValue("fondo_area_error_to_apply", errorToApply);
    }else{
      setValue("fondo_area_to_apply", 0);
      setValue("fondo_area_error_to_apply", 0);
    }
  }, [fondoAreaCurrentCps, fondoAreaReferenciaCps, fondoIntegralReferenciaCps, errorFondoAreaActual, errorFondoAreaReferencia, getLatestFondoFromList, tableRows, selectedIsotope, fondosList, selectedEquipment, type, currentFondoId]);

  useEffect(() => {
    if (selectedIsotope) {
      const selectedEnergy: string =
        isotoposList.find((iso) => iso.codigo === selectedIsotope?.codigo)
          ?.energia || "";

      if (selectedEnergy !== "") {
        const formattedEnergy = parseFloat(selectedEnergy).toFixed(1);
        setValue("energy", formattedEnergy);
      } else {
        setValue("energy", "");
      }
    }
  }, [selectedIsotope, isotoposInstalacionList]);

  // Cálculo de FONDO INTEGRAL 1 (cps)
  useEffect(() => {
    if (firstIntegralFondo && firstFondoCountingTime) {
      const firstIntegralCps = firstIntegralFondo / firstFondoCountingTime;
      firstCpsIntegralFondoValue = firstIntegralCps;
      setValue(
        "cps_integral_fondo_1",
        toExponentialUppercase(firstIntegralCps, 3)
      );
    }
  }, [firstIntegralFondo, firstFondoCountingTime]);

  // Cálculo de FONDO INTEGRAL 2 (cps)
  useEffect(() => {
    if (secondIntegralFondo && secondFondoCountingTime) {
      const integralCps = secondIntegralFondo / secondFondoCountingTime;
      secondCpsIntegralFondoValue = integralCps;
      setValue("cps_integral_fondo_2", toExponentialUppercase(integralCps, 3));
    }
  }, [secondIntegralFondo, secondFondoCountingTime]);

  // Cálculo de FONDO INTEGRAL MEDIO (cps)
  useEffect(() => {
    if (firstCpsIntegralFondoValue && secondCpsIntegralFondoValue) {
      const avgIntegralFondo = mean(
        firstCpsIntegralFondoValue,
        secondCpsIntegralFondoValue
      );
      setValue(
        "avg_integral_fondo",
        toExponentialUppercase(avgIntegralFondo, 3)
      );
    }
  }, [firstCpsIntegralFondo, secondCpsIntegralFondo]);

  const handleAddRow = () => {

    const newRow: TableRowGammaForm = {
      id: `NEW_${tableRows.length + 1}`,
      isotope_instalation: getValues("isotope_instalation") !== undefined ? getValues("isotope_instalation") : "",
      energy: getValues("energy") !== undefined ? getValues("energy") : "",
      fondo_integral_reference: getValues("fondo_integral_reference") !== undefined ? getValues("fondo_integral_reference") : "",
      fondo_integral_current_reference:
        getValues("fondo_integral_current_reference") !== undefined ? getValues("fondo_integral_current_reference") : "",
      fondo_integral_current_reference_calculated:
        getValues("fondo_integral_current_reference_calculated") !== undefined ? getValues("fondo_integral_current_reference_calculated") : "",
      fondo_integral: getValues("fondo_integral") !== undefined ? getValues("fondo_integral") : "",
      lid_variation: getValues("lid_variation") !== undefined ? getValues("lid_variation") : "",
      fondo_area_reference: getValues("fondo_area_reference") !== undefined ? getValues("fondo_area_reference") : "",
      fondo_area_reference_error: getValues("fondo_area_reference_error") !== undefined ? getValues("fondo_area_reference_error") : "",
      fondo_area_current_counts: getValues("fondo_area_current_counts") !== undefined ? getValues("fondo_area_current_counts") : "",
      fondo_area_current_cps: getValues("fondo_area_current_cps") !== undefined ? getValues("fondo_area_current_cps") : "",
      fondo_area_error: getValues("fondo_area_error") !== undefined ? getValues("fondo_area_error") : "",
      fondo_area_variation: getValues("fondo_area_variation") !== undefined ? getValues("fondo_area_variation") : "",
      fondo_integration_to_apply: getValues("fondo_integration_to_apply") !== undefined ? getValues("fondo_integration_to_apply") : "",
      fondo_area_to_apply: getValues("fondo_area_to_apply") !== undefined ? getValues("fondo_area_to_apply") : "",
      fondo_area_error_to_apply: getValues("fondo_area_error_to_apply") !== undefined ? getValues("fondo_area_error_to_apply") : "",
    };

    const updatedRows = [...tableRows, newRow];
    setValue("tableData", updatedRows);
    setValue("fondo_integral_reference", "");
    setValue("fondo_integral_current_reference", "");
    setValue("fondo_integral_current_reference_calculated", "");
    setValue("fondo_integral", "");
    setValue("lid_variation", "");
    setValue("fondo_area_reference", "");
    setValue("fondo_area_reference_error", "");
    setValue("fondo_area_current_counts", "");
    setValue("fondo_area_current_cps", "");
    setValue("fondo_area_error", "");
    setValue("fondo_area_variation", "");
    setValue("fondo_integration_to_apply", "");
    setValue("fondo_area_to_apply", "");
    setValue("fondo_area_error_to_apply", "");
  };

  const handleDeleteRow = async (id: number) => {
    if (typeof id !== "string") {
      await deleteFondoCalculation(id);
    }
    const updatedRows = tableRows.filter((row) => row.id !== id);
    setValue("tableData", updatedRows);
  };

  const handleExtract = (tableRowsGamma) => {
    setValue("tableData", tableRowsGamma);
  };

  const handleAskMoreCounts = (callback: (willHaveMoreCounts: boolean) => void) => {
    setMoreCountsCallback(() => callback);
    setShowMoreCountsDialog(true);
  };

  const handleMoreCountsResponse = (response: boolean) => {
    if (moreCountsCallback) {
      moreCountsCallback(response);
    }
    setShowMoreCountsDialog(false);
    setMoreCountsCallback(null);
  };

  return analysisType?.codigo !== KeysTabsAnalysis.GT ? (
    <>
      <FileUploadRPTFondos 
        onExtract={handleExtract} 
        isotopesList={isotoposList} 
        lastFondo={getLatestFondoFromList}
        fondosList={fondosList}
        currentEquipment={selectedEquipment}
        currentCalculationDate={currentCalculationDate}
        onAskMoreCounts={handleAskMoreCounts}
      />

      <ResponsiveFormContainer columns={5} sx={{mt: 2}}>
        <MAutocomplete
          name="isotope_instalation"
          options={isotoposList}
          label="Seleccione un Isótopo"
          getOptionLabel={(option) =>
            `${option.codigo}` || ""
          }
          disabled={disable}
        />
        <MTextField
          key={JSON.stringify(watch("isotope_instalation"))}
          name="energy"
          label="Energía (Kev)"
          placeholder="Energía (Kev)"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
      <ResponsiveFormContainer columns={5} mt={2}>
        <MTextField
          name="fondo_integral_reference"
          label="Fondo Integral Referencia (cps)"
          placeholder="Fondo Integral Referencia (cps)"
          fullWidth
          type="number"
          disabled={disable}
        />
        <MTextField
          name="fondo_integral_current_reference"
          label="Fondo Integral actual (cuentas)"
          placeholder="Fondo Integral actual (cuentas)"
          fullWidth
          type="number"
          disabled={disable}
        />
        <MTextField
          name="fondo_integral_current_reference_calculated"
          label="Fondo Integral actual (cps)"
          placeholder="Fondo Integral actual (cps)"
          fullWidth
          disabled
        />
        <MTextField
          name="fondo_integral"
          label="Fondo Integral (%var)"
          placeholder="Fondo Integral (%var)"
          fullWidth
          disabled
        />
        <MTextField
          name="lid_variation"
          label="Variación (%) LID"
          placeholder="Variación (%) LID"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
      <ResponsiveFormContainer columns={5} mt={2}>
        <MTextField
          name="fondo_area_reference"
          label="Fondo Área referencia (cps)"
          placeholder="Fondo Área referencia (cps)"
          fullWidth
          type="number"
          disabled={disable}
        />
        <MTextField
          name="fondo_area_reference_error"
          label="Error Fondo Área referencia (cps)"
          placeholder="Error Fondo Área referencia (cps)"
          fullWidth
          type="number"
          disabled={disable}
        />
        <MTextField
          name="fondo_area_current_counts"
          label="Fondo Área actual (cuentas)"
          placeholder="Fondo Área actual (cuentas)"
          fullWidth
          type="number"
          disabled={disable}
        />

        <MTextField
          name="fondo_area_current_cps"
          label="Fondo Área actual (cps)"
          placeholder="Fondo Área actual (cps)"
          fullWidth
          disabled
        />

        <MTextField
          name="fondo_area_error"
          label="Error Fondo Área (cps)"
          placeholder="Error Fondo Área (cps)"
          fullWidth
          disabled
        />

        <MTextField
          name="fondo_integration_to_apply"
          label="A aplicar Fondo Integral (cps)"
          placeholder="A aplicar Fondo Integral (cps)"
          fullWidth
          disabled
        />

        <MTextField
          name="fondo_area_variation"
          label="Variación (%) Fondo Área"
          placeholder="Variación (%) Fondo Área"
          fullWidth
          disabled
        />

        <MTextField
          name="fondo_area_to_apply"
          label="A aplicar Fondo Área (cps)"
          placeholder="A aplicar Fondo Área (cps)"
          fullWidth
          disabled
        />
        <MTextField
          name="fondo_area_error_to_apply"
          label="A aplicar Error Fondo Área (cps)"
          placeholder="A aplicar Error Fondo Área (cps)"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddRow}
        style={{ marginTop: "16px" }}
      >
        Agregar a la Tabla
      </Button>

      <DataGrid
        rows={tableRows}
        columns={columnsGammaForm(handleDeleteRow)}
        disableRowSelectionOnClick
        hideFooterPagination
        style={{ marginTop: "16px", height: 400 }}
      />

      {/* Diálogo para preguntar si habrá más contajes */}
      <Dialog
        open={showMoreCountsDialog}
        onClose={() => handleMoreCountsResponse(false)}
        aria-labelledby="more-counts-dialog-title"
      >
        <DialogTitle id="more-counts-dialog-title">
          Contaje único detectado
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Este es el único contaje del mes. ¿Habrá más contajes este mes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleMoreCountsResponse(false)} variant="outlined" color="error">
            No
          </Button>
          <Button onClick={() => handleMoreCountsResponse(true)} variant="contained" autoFocus>
            Sí
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : (
    <ResponsiveFormContainer columns={5}>
      <MAutocomplete
        name="month"
        options={monthsTable}
        label="Seleccione un mes"
        validation={{ required: "El mes es requerido" }}
      />
      <MAutocomplete
        name="year"
        options={yearsTable}
        label="Año"
        getOptionLabel={(option) => `${option}`}
        validation={{ required: "El año es requerido" }}
      />
      <MTextField
        name="integral_fondo_1"
        placeholder="Fondo integral 1"
        label="Fondo integral 1"
        type="number"
        validation={{ required: "El fondo integral es requerido" }}
        fullWidth
      />
      <MTextField
        name="counting_time"
        placeholder="Tiempo contaje fondo 1"
        label="Tiempo contaje fondo 1"
        type="number"
        validation={{ required: "El tiempo de contaje es requerido" }}
        fullWidth
      />
      <MTextField
        name="integral_fondo_2"
        placeholder="Fondo integral 2"
        label="Fondo integral 2"
        type="number"
        validation={{ required: "El fondo integral es requerido" }}
        fullWidth
      />
      <MTextField
        name="counting_time_fondo_2"
        placeholder="Tiempo contaje fondo 2"
        label="Tiempo contaje fondo 2"
        type="number"
        validation={{ required: "El tiempo de contaje es requerido" }}
        fullWidth
      />
      <MTextField
        name="cps_integral_fondo_1"
        placeholder="Fondo integral 1 (cps)"
        label="Fondo integral 1 (cps)"
        type="number"
        fullWidth
        disabled
      />
      <MTextField
        name="cps_integral_fondo_2"
        placeholder="Fondo integral 2 (cps)"
        label="Fondo integral 2 (cps)"
        type="number"
        fullWidth
        disabled
      />
      <MTextField
        name="avg_integral_fondo"
        placeholder="Fondo integral medio (cps)"
        label="Fondo integral medio (cps)"
        type="number"
        fullWidth
        disabled
      />
    </ResponsiveFormContainer>
  );
};

export default GammaForm;
